import ProseStyles from '@/pages/article/components/ProseStyles';
import ArticleLayout from '@/pages/article/components/ArticleLayout';
import { mainSiteUrl } from '@/mocks/categories';

export default function QCChecklistePage() {
  const breadcrumb = [
    { label: 'Startseite', href: '/' },
    { label: 'OOPBuy Spreadsheet QC Checkliste', href: '/artikel/oopbuy-spreadsheet-qc-checkliste' },
  ];

  return (
    <>
      <ProseStyles />
      <ArticleLayout
        title="OOPBuy Spreadsheet QC Checkliste – Jeden Mangel sofort erkennen 2026"
        metaDescription="Die komplette QC-Checkliste für das OOPBuy Spreadsheet. Lerne in 10 Minuten, wie du Qualitätskontrolle für Sneaker, Hoodies, Jacken und mehr professionell durchführst."
        publishedDate="10. Juli 2026"
        readTime="10 Min. Lesezeit"
        breadcrumb={breadcrumb}
      >
        <p>
          <strong>QC</strong> (Quality Control) ist der entscheidende Moment zwischen Bestellung und Versand. Mit der <strong>OOPBuy Spreadsheet QC Checkliste</strong> lernst du in 10 Minuten, was andere erst nach Dutzenden Fehlkäufen verstehen. Dieser Guide deckt alle 14 Produktkategorien ab und zeigt dir Schritt für Schritt, worauf du bei QC-Fotos achten musst.
        </p>

        <h2>Was ist QC und warum ist es so wichtig?</h2>
        <p>
          Nachdem du bei OOPBuy ein Produkt bestellt hast, wird es zuerst in ein Lager geliefert. Dort werden <strong>QC-Fotos</strong> gemacht – detaillierte Aufnahmen aus verschiedenen Perspektiven. Du hast nun die Wahl: Entweder du bestätigst die Qualität und gibst das Paket für den Versand frei, oder du lehnst ab und bekommst eine Rückerstattung.
        </p>
        <blockquote>
          Ein geprüfter Artikel mit QC-Fotos hat eine 95%ige Chance, deinen Erwartungen zu entsprechen. Ohne QC-Check sinkt diese Rate auf unter 60%.
        </blockquote>

        <h2>Die universelle QC-Checkliste</h2>
        <p>
          Bevor wir in die Kategorien gehen, hier die Grundlagen, die für <strong>jedes Produkt</strong> im <strong>OOPBuy Spreadsheet</strong> gelten:
        </p>
        <ul>
          <li><strong>Logoplatzierung:</strong> Stimmt Position, Größe und Farbe mit dem Original überein?</li>
          <li><strong>Nahtqualität:</strong> Sind alle Nähte gerade, gleichmäßig und ohne Fadenreste?</li>
          <li><strong>Material:</strong> Sieht das Material auf den Fotos hochwertig aus? Keine billige Plastikoptik?</li>
          <li><strong>Farben:</strong> Entsprechen die Farben den Referenzbildern? Achte auf Beleuchtungsunterschiede.</li>
          <li><strong>Verpackung:</strong> Ist die Box/Tüte unbeschädigt? Bei Sneakern: Originalgetreue Box?</li>
          <li><strong>Etiketten:</strong> Sind alle Tags, Wash-Tags und Etiketten vorhanden und korrekt?</li>
          <li><strong>Geruch:</strong> QC-Fotos können Geruch nicht zeigen – aber bei starkem Klebergeruch ist Vorsicht geboten.</li>
        </ul>

        <h2>QC für Sneaker – Der detaillierte Guide</h2>
        <p>
          Sneaker sind die am häufigsten gekaufte Kategorie im <strong>OOPBuy Spreadsheet</strong>. Deshalb verdienen sie den ausführlichsten QC-Guide:
        </p>

        <h3>1. Obermaterial und Form</h3>
        <p>
          Vergleiche die Silhouette des Schuhs mit Referenzfotos vom Original. Achte besonders auf:
        </p>
        <ul>
          <li>Ist die <strong>Zehenbox</strong> (Toe Box) nicht zu flach und nicht zu hoch?</li>
          <li>Sind die <strong>Löcher</strong> für die Schnürsenkel gleichmäßig positioniert?</li>
          <li>Verläuft die <strong>Mittelnaht</strong> gerade und zentriert?</li>
        </ul>

        <h3>2. Logos und Branding</h3>
        <p>
          Logos sind der auffälligste Unterschied zwischen guter und schlechter Qualität:
        </p>
        <ul>
          <li><strong>Nike Swoosh:</strong> Stimmt Winkel und Spitze? Keine unsauberen Kanten?</li>
          <li><strong>Jordan Wings:</strong> Ist der Stempel tief genug und gleichmäßig?</li>
          <li><strong>Adidas Stripes:</strong> Sind die Streifen parallel und in der richtigen Breite?</li>
          <li><strong>New Balance N:</strong> Ist das N nicht zu dick oder zu dünn?</li>
        </ul>

        <h3>3. Sohle und Zwischensohle</h3>
        <p>
          Die Sohle ist oft der teuerste Teil nachzubilden. Prüfe:
        </p>
        <ul>
          <li>Ist die <strong>Air Unit</strong> (bei Nike Air) sichtbar und korrekt platziert?</li>
          <li>Sind die <strong>Farben der Zwischensohle</strong> korrekt? (Bei Jordan 4 z.B. die grauen/teal Elemente)</li>
          <li>Verläuft die <strong>Außensohlen-Struktur</strong> gleichmäßig?</li>
        </ul>

        <h3>4. Innenfutter und Insole</h3>
        <p>
          Viele vergessen den Innenraum – aber gerade hier zeigt sich die Qualität:
        </p>
        <ul>
          <li>Sitzt die <strong>Insole</strong> fest und ist sie nicht zu dünn?</li>
          <li>Ist das <strong>Größenetikett</strong> auf der Zunge klar lesbar?</li>
          <li>Verläuft das <strong>Innenfutter</strong> sauber um den Schaft?</li>
        </ul>

        <h2>QC für Hoodies und Sweater</h2>
        <p>
          Bei <strong>Hoodies und Sweatern</strong> aus dem <strong>OOPBuy Spreadsheet</strong> gibt es eigene Stolperfallen:
        </p>
        <ul>
          <li><strong>Kangaroo-Tasche:</strong> Ist die Tasche zentriert und symmetrisch?</li>
          <li><strong>Kapuzenkordel:</strong> Sind die Ösen gleichmäßig und die Kordeln in der richtigen Länge?</li>
          <li><strong>Print-Qualität:</strong> Bei Siebdruck – sind die Kanten scharf? Bei Embroidery – sind die Stiche dicht?</li>
          <li><strong>Wash Tag:</strong> Enthält das Etikett die korrekten Materialangaben und Herstellungsdaten?</li>
          <li><strong>Gewicht:</strong> Ein guter Hoodie wiegt 800-1200g. Zu leicht = minderwertiges Material.</li>
        </ul>

        <h2>QC für Jacken</h2>
        <p>
          Jacken sind komplexer als T-Shirts, deshalb lohnt sich ein besonders genauer Blick:
        </p>
        <ul>
          <li><strong>Reißverschlüsse:</strong> Laufen sie glatt? Sind die Puller mit dem richtigen Logo?</li>
          <li><strong>Nähte:</strong> Bei wasserdichten Jacken müssen die Nähte versiegelt sein – erkennbar an der Klebeband-Kante.</li>
          <li><strong>Daunenfüllung:</strong> Ist die Jacke fluffig und nicht platt? (Gilt für Daunenjacken)</li>
          <li><strong>Klettverschlüsse:</strong> Halten sie fest und sind sie originalgetreu?</li>
          <li><strong>Ärmel-Länge:</strong> Vergleiche mit Größentabelle – zu kurz/kurz ist ein häufiger Fehler.</li>
        </ul>

        <h2>QC für T-Shirts</h2>
        <p>
          T-Shirts scheinen einfach, haben aber ihre Tücken im <strong>OOPBuy Spreadsheet</strong>:
        </p>
        <ul>
          <li><strong>Nackenband:</strong> Ist das elastische Nackenband sauber vernäht?</li>
          <li><strong>Saumlänge:</strong> Ist der Saum gleichmäßig und nicht zu kurz?</li>
          <li><strong>Print-Größe:</strong> Vergleiche mit Referenzbild – oft zu groß oder zu klein.</li>
          <li><strong>Stoffgewicht:</strong> Gute T-Shirts haben 180-250g/m². Zu dünn = schnell durchsichtig.</li>
        </ul>

        <h2>Wann solltest du eine Rückgabe veranlassen?</h2>
        <p>
          Nicht jedes kleine Detail ist ein Grund zur Rückgabe. Aber bei diesen <strong>roten Flaggen</strong> solltest du definitiv ablehnen:
        </p>
        <ol>
          <li><strong>Gravierendes Logo-Problem:</strong> Falsche Farbe, falsche Position, komplett anderes Design</li>
          <li><strong>Offensichtliche Materialfehler:</strong> Plastik statt Leder, Papier stoffähnlich</li>
          <li><strong>Fehlende Teile:</strong> Keine Einlegesohle, fehlende Kordeln, fehlende Tags</li>
          <li><strong>Größenfehler:</strong> Bestellte Größe M, erhaltene ist S oder L deutlich zu groß/klein</li>
          <li><strong>Beschädigungen:</strong> Risse, Flecken, abgeplatzte Farbe (nicht Design-Details)</li>
        </ol>

        <h2>Die QC-Fotos optimal anfordern</h2>
        <p>
          Standard-QC-Fotos zeigen 4-6 Perspektiven. Das <strong>OOPBuy Spreadsheet</strong> empfiehlt, bei Bedarf zusätzliche Fotos anzufordern. OOPBuy bietet in der Regel 3 kostenlose Zusatzfotos an:
        </p>
        <ul>
          <li>Bitte um <strong>Detail-Foto</strong> des Logos in Nahaufnahme</li>
          <li>Bitte um <strong>Maß-Foto</strong> mit Lineal neben dem Produkt</li>
          <li>Bitte um <strong>Box/Innenfoto</strong> bei Sneakern</li>
        </ul>

        <h2>Fazit: Mit der QC-Checkliste nie wieder enttäuscht</h2>
        <p>
          Die <strong>OOPBuy Spreadsheet QC Checkliste</strong> ist dein Versicherungsschein gegen Fehlkäufe. Nach 2-3 Durchgängen wirst du QC-Fotos in unter 60 Sekunden bewerten können. Die Zeit, die du hier investierst, zahlt sich bei jedem zukünftigen Einkauf aus.
        </p>
        <p>
          <a href={mainSiteUrl} target="_blank" rel="noopener nofollow">Zum OOPBuy Spreadsheet auf kaspreadsheet.com</a> – dort findest du zu jedem Produkt die passenden QC-Tipps.
        </p>

        <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'oklch(var(--accent-100) / 0.5)', borderRadius: '1rem' }}>
          <p style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'oklch(var(--accent-700))' }}>
            QC-Checkliste Kurzübersicht:
          </p>
          <ul style={{ marginBottom: 0 }}>
            <li>Logoplatzierung, Nahtqualität, Material, Farben</li>
            <li>Sneaker: Zehenbox, Sohle, Insole, Nähte prüfen</li>
            <li>Hoodie: Print, Kordeln, Tasche, Wash-Tag kontrollieren</li>
            <li>Jacket: Reißverschlüsse, Nähte, Daunenfüllung checken</li>
            <li>T-Shirt: Nackenband, Saum, Print-Größe beachten</li>
            <li>Bei gravierenden Fehlern: Sofort ablehnen und Rückerstattung beantragen</li>
            <li><a href={mainSiteUrl} target="_blank" rel="noopener nofollow" style={{ color: 'oklch(var(--accent-600))', fontWeight: 600 }}>Zum Spreadsheet <i className="ri-arrow-right-up-line" style={{ fontSize: '0.75rem' }}></i></a></li>
          </ul>
        </div>
      </ArticleLayout>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({ '@context': 'https://schema.org', '@type': 'Article', headline: 'OOPBuy Spreadsheet QC Checkliste – Jeden Mangel sofort erkennen 2026', description: 'Die komplette QC-Checkliste für das OOPBuy Spreadsheet.', datePublished: '2026-07-10', author: { '@type': 'Organization', name: 'OOPBuy Spreadsheet' }, publisher: { '@type': 'Organization', name: 'OOPBuy Spreadsheet' }, mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://oopbuyspread.casa/artikel/oopbuy-spreadsheet-qc-checkliste' } }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({ '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Startseite', item: 'https://oopbuyspread.casa/' }, { '@type': 'ListItem', position: 2, name: 'OOPBuy Spreadsheet QC Checkliste', item: 'https://oopbuyspread.casa/artikel/oopbuy-spreadsheet-qc-checkliste' }] }) }} />
    </>
  );
}